/*
 *  -----------------------------------------------------------------------
 *    <copyright file="add-crf-assessment/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import { Checkbox } from '@progress/kendo-react-inputs';

import { useBoolean, useTranslation } from 'hooks';
import { StudyCrfFields } from 'queries/generated/fragments.generated';
import { studyCrfAssessments_OpraQueries_crfAssessments_StudyAssessment } from 'queries/generated/study-crfs.generated';
import { GET_STUDY_CRF_ASSESSMENTS, SAVE_STUDY_CRF_ASSESSMENT } from 'queries/study-crfs';
import NotificationService from 'utils/notification';
import { checkIfNameExist, checkRequiredFields, validateFields } from 'utils/validation';
import Button from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import TextField from 'views/components/core/text-field';

import './styles.scss';

interface AddCrfAssessmentDialogInterface {
    open: boolean;
    close(action: string): any;
    studyId: string;
    currentCrf: StudyCrfFields;
    currentCrfAssessment: studyCrfAssessments_OpraQueries_crfAssessments_StudyAssessment;
    assessments: studyCrfAssessments_OpraQueries_crfAssessments_StudyAssessment[];
}

const AddCrfAssessmentDialog = ({
    open,
    close,
    studyId,
    currentCrf,
    currentCrfAssessment = null,
    assessments,
}: AddCrfAssessmentDialogInterface) => {
    const { t } = useTranslation();
    const initialState = { active: true, name: '', endPoint: false, safety: false, lab: false, sendToVendor: false };
    const initialErrors = { name: '' };
    const [state, setState] = useState({ ...initialState });
    const [errors, setErrors] = useState({ ...initialErrors } as any);
    const disabledSave = useBoolean(true);
    const [saveAssessment] = useMutation(SAVE_STUDY_CRF_ASSESSMENT);

    useEffect(() => {
        if (currentCrfAssessment)
            setState({
                active: currentCrfAssessment.active,
                name: currentCrfAssessment.name,
                endPoint: currentCrfAssessment.endPoint,
                safety: currentCrfAssessment.safety,
                lab: currentCrfAssessment.lab,
                sendToVendor: currentCrfAssessment.sendToVendor,
            });
        else setState({ ...initialState });
    }, [currentCrfAssessment]);

    const onClose = (action?: string) => {
        if (action !== 'error') {
            setState({ ...initialState });
            setErrors({ ...initialErrors });
        }
        close(action);
    };

    const validate = (value?: string, field?: string, length?: number) => {
        const error = validateFields(value, field, length);
        setErrors({
            ...errors,
            ...error,
        });
    };

    const onChange = (fieldName: string) => (event: any) => {
        const { value } = event.target;
        const length = 250;
        disabledSave.setFalse();
        setState({
            ...state,
            [fieldName]: value,
        });
        validate(value, fieldName, length);
    };

    const onCheckboxChange = (fieldName: 'active' | 'endPoint' | 'safety' | 'lab' | 'sendToVendor') => () => {
        // 'endPoint' | 'safety' | 'sentToVendor'
        disabledSave.setFalse();
        setState({ ...state, [fieldName]: !state[fieldName] });
    };

    const submit = () => {
        disabledSave.setTrue();
        if (checkRequiredFields({ fields: ['name'], state, setErrors, errors })) {
            const isNameExist = checkIfNameExist({ ...state, name: state.name.trim(), id: null }, assessments, 'name');

            if (
                (isNameExist && currentCrfAssessment === null) ||
                (currentCrfAssessment && currentCrfAssessment.name !== state.name && isNameExist)
            ) {
                setErrors({ ...errors, name: t('admin:assessmentNameAlreadyExists') });
                return;
            }

            saveAssessment({
                variables: {
                    studyId,
                    assessment: {
                        id: currentCrfAssessment?.id || null,
                        crfId: currentCrf?.id,
                        active: state.active,
                        name: state.name,
                        endPoint: state.endPoint,
                        safety: state.safety,
                        lab: state.lab,
                        sendToVendor: state.sendToVendor,
                    },
                },
                refetchQueries: [
                    {
                        query: GET_STUDY_CRF_ASSESSMENTS,
                        variables: { studyId: studyId ?? null },
                    },
                ],
            })
                .then(() => {
                    onClose(currentCrfAssessment ? 'edit' : 'success');
                })
                .catch(() => {
                    onClose('error');
                });
        }
    };

    return (
        <div>
            {open && (
                <CustomDialog
                    onClose={() => {
                        onClose();
                    }}
                    fullWidth
                    maxWidth="md"
                    scroll="paper"
                    open={open}
                    className="custom-width-popup add-crf-assessment-modal max-w700"
                >
                    <DialogTitle id="add-crf-modal-title">
                        {currentCrfAssessment
                            ? t('admin:editAssessmentNumber', { recordId: currentCrfAssessment.recordId })
                            : t('admin:addAssessment')}
                        <FontAwesomeIcon
                            icon={['fal', 'times']}
                            className="fa-lg close-icon-cv"
                            onClick={() => {
                                onClose();
                            }}
                            data-testid="close-dialog"
                        />
                    </DialogTitle>
                    <DialogContent className="add-crf-dialog-content">
                        <div className="checkboxes-wrapper">
                            <div className="left-part">
                                <Checkbox
                                    rounded={null}
                                    id="crf-active-checkbox"
                                    className="cell-checkbox mr no-border"
                                    checked={state.active}
                                    onChange={onCheckboxChange('active')}
                                    label={t('admin:active')}
                                    data-testid="crf-active-checkbox"
                                    size="medium"
                                />
                            </div>
                        </div>
                        <TextField
                            label={t('common:name')}
                            id="add-assessment-name"
                            fullWidth
                            required
                            value={state.name}
                            onChange={onChange('name')}
                            placeholder={t('admin:enterName')}
                            helperText={
                                <CharactersRemaining
                                    id="add-assessment-name-dialog-help-text"
                                    length={state.name?.length}
                                    max={250}
                                    error={errors.name}
                                />
                            }
                            variant="outlined"
                            color="secondary"
                            error={!!errors.name}
                            className="before-select mb"
                        />
                        <div className="checkboxes-wrapper">
                            <div className="left-part">
                                <Checkbox
                                    rounded={null}
                                    id="crf-end-point-checkbox"
                                    className="cell-checkbox no-border mr"
                                    checked={state.endPoint}
                                    onChange={onCheckboxChange('endPoint')}
                                    label={t('admin:endPoint')}
                                    data-testid="crf-end-point-checkbox"
                                    size="medium"
                                />
                                <Checkbox
                                    rounded={null}
                                    id="crf-safety-checkbox"
                                    className="cell-checkbox no-border mr"
                                    checked={state.safety}
                                    onChange={onCheckboxChange('safety')}
                                    label={t('admin:safety')}
                                    data-testid="crf-safety-checkbox"
                                    size="medium"
                                />
                                <Checkbox
                                    rounded={null}
                                    id="crf-lab-checkbox"
                                    className="cell-checkbox no-border mr"
                                    checked={state.lab}
                                    onChange={onCheckboxChange('lab')}
                                    label={t('admin:lab')}
                                    data-testid="crf-lab-checkbox"
                                    size="medium"
                                />
                                <Checkbox
                                    rounded={null}
                                    id="crf-send-vendor-checkbox"
                                    className="cell-checkbox no-border mr"
                                    checked={state.sendToVendor}
                                    onChange={onCheckboxChange('sendToVendor')}
                                    label={t('admin:sendToVendor')}
                                    data-testid="crf-send-vendor-checkbox"
                                    size="medium"
                                />
                            </div>
                        </div>
                        <DialogActions
                            className={`add-crf-dialog-content-actions ${!currentCrfAssessment && 'flex-end'}`}
                        >
                            <ModalUpdatedInfo item={currentCrfAssessment} />
                            <Button
                                id="submit-assessment-btn"
                                className="add-crf-btn"
                                disabled={
                                    Object.keys(errors).filter((item) => errors[item]).length > 0 || disabledSave.value
                                }
                                text={currentCrfAssessment ? 'update' : 'add'}
                                onClick={submit}
                            />
                        </DialogActions>
                    </DialogContent>
                </CustomDialog>
            )}
        </div>
    );
};

export default AddCrfAssessmentDialog;
